/**
 * Rotas do módulo Publicações (motor de publicação) — Story 3.1.
 *
 * | Rota                                             | Gate                    | Quem usa      |
 * |--------------------------------------------------|-------------------------|---------------|
 * | `/publicacoes/ocasioes`                          | `publicacao:configurar` | administrador |
 * | `/publicacoes/ocasioes/:ocasiaoId/elegiveis`     | `publicacao:configurar` | administrador |
 * | `/publicacoes/dias-nao-uteis`                    | `publicacao:configurar` | administrador |
 *
 * A segunda tela é alcançada pelo card da ocasião, e **não** por item próprio
 * de menu: a pergunta é "quem é elegível a ESTA ocasião", então a ocasião é o
 * caminho. Dois níveis de profundidade, que é o teto que o UX declara.
 *
 * 🔴 **O gate destas telas é `publicacao:configurar`, e continua sendo.** Elas
 * **são** configuração — o UX as chama de "Configuração de ocasião" e diz que
 * "configuração é irmã da fila, não filha". A promessa que este comentário fazia
 * desde a story 3.1 — *"quando o épico 4 construir a fila, ele semeia
 * `publicacao:read` e a consome na mesma story"* — **foi cumprida pela story
 * 4.4**: `publicacao:read` e `publicacao:agendar` são semeadas em
 * `publicacao_permissions_seed.py` e consumidas por `GET
 * /publicacoes/agendamentos` e `POST /publicacoes/ocasioes/{id}/gerar`.
 *
 * A separação é do PRD e não é cosmética: quem monta a fila e anexa arte
 * (Marketing, uso mensal) não é quem define ocasiões e roteamento
 * (administrador, uso anual). A tela da fila, que vai gatear em
 * `publicacao:read`, é da story **4.7**.
 *
 * ⚠️ A rota usa `permission: { resource, action }` (**objeto**); o Sidebar usa
 * `requiredPermission: "resource:action"` (**string**). Não são intercambiáveis
 * — e o `path` é absoluto apesar de `basePath` existir.
 *
 * ⚠️ `lazy()` mora aqui; `Suspense` **não** — ele é centralizado em
 * `routes/index.tsx`, envolvido por `RequirePermission`.
 */
import { lazy } from "react";

import { ModuleRoutes } from "../types";

const FilaPage = lazy(() => import("@/pages/publicacoes/FilaPage"));
const OcasioesPage = lazy(() => import("@/pages/publicacoes/OcasioesPage"));
const ElegiveisPage = lazy(() => import("@/pages/publicacoes/ElegiveisPage"));
const OcasiaoEditorPage = lazy(() => import("@/pages/publicacoes/OcasiaoEditorPage"));
const RegrasDestinoPage = lazy(() => import("@/pages/publicacoes/RegrasDestinoPage"));
const DiasNaoUteisPage = lazy(() => import("@/pages/publicacoes/DiasNaoUteisPage"));

export const publicacoesRoutes: ModuleRoutes = {
  moduleId: "publicacoes",
  basePath: "/publicacoes",
  routes: [
    // Story 4.7 — a fila (FR49). **A primeira tela do módulo gateada em
    // `publicacao:read`**, e não em `configurar`: quem monta a fila e anexa arte
    // (Marketing, uso mensal) não é quem define ocasiões e roteamento
    // (administrador, uso anual). Dar `configurar` ao Marketing só para ele ver
    // a fila entregaria de brinde o destino de toda publicação futura.
    //
    // Vem PRIMEIRO na lista porque é a porta do módulo: uso mensal contra o uso
    // anual das telas de configuração.
    {
      path: "/publicacoes/fila",
      element: <FilaPage />,
      permission: { resource: "publicacao", action: "read" },
    },
    {
      path: "/publicacoes/ocasioes",
      element: <OcasioesPage />,
      permission: { resource: "publicacao", action: "configurar" },
    },
    {
      path: "/publicacoes/ocasioes/:ocasiaoId/elegiveis",
      element: <ElegiveisPage />,
      permission: { resource: "publicacao", action: "configurar" },
    },
    // Story 3.3 — criar e editar por configuração (FR57).
    //
    // 🔴 `/nova` vem ANTES de `/:ocasiaoId/editar` e a ordem importa: o
    // react-router casa por especificidade, mas a lição de `/users/me` (story
    // 1.6, que respondeu 422 por semanas em produção) é declarar o literal
    // primeiro e não depender do algoritmo de desempate.
    {
      path: "/publicacoes/ocasioes/nova",
      element: <OcasiaoEditorPage />,
      permission: { resource: "publicacao", action: "configurar" },
    },
    {
      path: "/publicacoes/ocasioes/:ocasiaoId/editar",
      element: <OcasiaoEditorPage />,
      permission: { resource: "publicacao", action: "configurar" },
    },
    // Story 4.1 — a regra de destino (FR31, FR32, FR34).
    //
    // 🔴 **Irmã das ocasiões, não filha.** O UX é explícito: "configuração é
    // irmã da fila, não filha". A regra de destino não pertence a nenhuma
    // ocasião — ela vale para todas —, então pendurá-la em
    // `/ocasioes/:id/destino` seria modelar na URL uma relação que não existe,
    // e a story 4.4 teria de mudá-la de lugar ao construir a fila.
    {
      path: "/publicacoes/regras-destino",
      element: <RegrasDestinoPage />,
      permission: { resource: "publicacao", action: "configurar" },
    },
    // Story 6.3 — o calendário de dias não úteis (FR40).
    //
    // 🔴 **Irmã das ocasiões, não filha — pelo mesmo argumento da regra de
    // destino.** O calendário não pertence a nenhuma ocasião: ele vale para
    // todas, e a partir da story 6.4 alcança qualquer publicação que caia num
    // dos dias. Pendurá-la em `/ocasioes/:id/dias-nao-uteis` modelaria na URL
    // uma relação que não existe, e a 6.4 teria de mudá-la de lugar.
    //
    // ⚠️ Gate `publicacao:configurar`, como as irmãs de configuração: declarar
    // que 25/12 não é dia útil é ato administrativo de alcance ANUAL, não o
    // gesto mensal de quem monta a fila (`publicacao:read`/`agendar`).
    {
      path: "/publicacoes/dias-nao-uteis",
      element: <DiasNaoUteisPage />,
      permission: { resource: "publicacao", action: "configurar" },
    },
  ],
};
